import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Pencil, Eraser, Trash2, Undo2, Redo2, Download, Type,
  Circle, Square, Minus, Maximize2, Minimize2, ImagePlus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

type Tool = "pen" | "eraser" | "text" | "line" | "rect" | "circle";

interface Point { x: number; y: number; }
interface DrawAction { imageData: ImageData; }

const COLORS = [
  "#1a1a2e", "#e94560", "#0f3460", "#16c79a",
  "#f5a623", "#8b5cf6", "#ec4899", "#ffffff",
];
const SIZES = [2, 4, 6, 10, 16];

interface InteractiveWhiteboardProps {
  className?: string;
  height?: number;
}

export default function InteractiveWhiteboard({ className = "", height = 400 }: InteractiveWhiteboardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [lineWidth, setLineWidth] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [undoStack, setUndoStack] = useState<DrawAction[]>([]);
  const [redoStack, setRedoStack] = useState<DrawAction[]>([]);
  const [bgFileName, setBgFileName] = useState<string | null>(null);

  const lastPoint = useRef<Point | null>(null);
  const startPoint = useRef<Point | null>(null);
  const preShapeImage = useRef<ImageData | null>(null);

  const drawBackground = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number) => {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    const img = bgImageRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      const dx = (w - dw) / 2;
      const dy = (h - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
    }
  }, []);

  // Resize canvas
  useEffect(() => {
    const resize = () => {
      const container = containerRef.current;
      const canvas = canvasRef.current;
      if (!container || !canvas) return;
      const rect = container.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = isFullscreen ? window.innerHeight - 120 : height;

      const ctx = canvas.getContext("2d");
      let saved: ImageData | undefined;
      if (ctx && canvas.width > 0 && canvas.height > 0) {
        saved = ctx.getImageData(0, 0, canvas.width, canvas.height);
      }

      canvas.width = w;
      canvas.height = h;

      if (ctx) {
        drawBackground(ctx, w, h);
        if (saved) ctx.putImageData(saved, 0, 0);
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height, isFullscreen, drawBackground]);

  const getCtx = () => canvasRef.current?.getContext("2d") ?? null;

  const getPos = (e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const saveState = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setUndoStack((prev) => [...prev.slice(-30), { imageData }]);
    setRedoStack([]);
  }, []);

  const handleUndo = () => {
    const ctx = getCtx(); const canvas = canvasRef.current;
    if (!ctx || !canvas || undoStack.length === 0) return;
    const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setRedoStack((prev) => [...prev, { imageData: currentState }]);
    ctx.putImageData(undoStack[undoStack.length - 1].imageData, 0, 0);
    setUndoStack((prev) => prev.slice(0, -1));
  };

  const handleRedo = () => {
    const ctx = getCtx(); const canvas = canvasRef.current;
    if (!ctx || !canvas || redoStack.length === 0) return;
    const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setUndoStack((prev) => [...prev, { imageData: currentState }]);
    ctx.putImageData(redoStack[redoStack.length - 1].imageData, 0, 0);
    setRedoStack((prev) => prev.slice(0, -1));
  };

  const clearCanvas = () => {
    const ctx = getCtx(); const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    saveState();
    drawBackground(ctx, canvas.width, canvas.height);
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `whiteboard-${Date.now()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // --- Background image/PDF upload ---
  const handleBgUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = "";

    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");

    if (!isImage && !isPdf) {
      toast({ title: "نوع غير مدعوم", description: "يرجى اختيار صورة أو ملف PDF", variant: "destructive" });
      return;
    }

    if (isPdf) {
      // Render first page of PDF to canvas via an offscreen canvas
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const pdfjsLib = await import("pdfjs-dist");
          pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(reader.result as ArrayBuffer) }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 2 });
          const offscreen = document.createElement("canvas");
          offscreen.width = viewport.width;
          offscreen.height = viewport.height;
          const offCtx = offscreen.getContext("2d")!;
          await page.render({ canvasContext: offCtx, viewport, canvas: offscreen } as any).promise;

          const img = new Image();
          img.onload = () => {
            bgImageRef.current = img;
            setBgFileName(file.name);
            saveState();
            const ctx = getCtx();
            const canvas = canvasRef.current;
            if (ctx && canvas) drawBackground(ctx, canvas.width, canvas.height);
          };
          img.src = offscreen.toDataURL();
        } catch {
          toast({ title: "خطأ في فتح PDF", description: "تعذر قراءة ملف PDF", variant: "destructive" });
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        bgImageRef.current = img;
        setBgFileName(file.name);
        saveState();
        const ctx = getCtx();
        const canvas = canvasRef.current;
        if (ctx && canvas) drawBackground(ctx, canvas.width, canvas.height);
      };
      img.onerror = () => {
        toast({ title: "خطأ", description: "تعذر فتح الصورة", variant: "destructive" });
      };
      img.src = url;
    }
  };

  const removeBg = () => {
    bgImageRef.current = null;
    setBgFileName(null);
    saveState();
    const ctx = getCtx(); const canvas = canvasRef.current;
    if (ctx && canvas) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  // --- Drawing handlers ---
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    saveState();
    const pos = getPos(e);
    setIsDrawing(true);
    lastPoint.current = pos;
    startPoint.current = pos;

    if (tool === "line" || tool === "rect" || tool === "circle") {
      preShapeImage.current = ctx.getImageData(0, 0, canvasRef.current!.width, canvasRef.current!.height);
    }
    if (tool === "pen" || tool === "eraser") {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
      ctx.lineWidth = tool === "eraser" ? lineWidth * 3 : lineWidth;
    }
    if (tool === "text") {
      const text = prompt("اكتب النص:");
      if (text) {
        ctx.font = `${lineWidth * 5 + 12}px 'Cairo', 'Tajawal', sans-serif`;
        ctx.fillStyle = color;
        ctx.textAlign = "right";
        ctx.direction = "rtl";
        ctx.fillText(text, pos.x, pos.y);
      }
      setIsDrawing(false);
    }
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);

    if (tool === "pen" || tool === "eraser") {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    if ((tool === "line" || tool === "rect" || tool === "circle") && preShapeImage.current && startPoint.current) {
      ctx.putImageData(preShapeImage.current, 0, 0);
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      if (tool === "line") {
        ctx.moveTo(startPoint.current.x, startPoint.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else if (tool === "rect") {
        ctx.strokeRect(startPoint.current.x, startPoint.current.y, pos.x - startPoint.current.x, pos.y - startPoint.current.y);
      } else if (tool === "circle") {
        const rx = Math.abs(pos.x - startPoint.current.x) / 2;
        const ry = Math.abs(pos.y - startPoint.current.y) / 2;
        const cx = startPoint.current.x + (pos.x - startPoint.current.x) / 2;
        const cy = startPoint.current.y + (pos.y - startPoint.current.y) / 2;
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    lastPoint.current = pos;
  };

  const handleEnd = () => {
    setIsDrawing(false);
    lastPoint.current = null;
    startPoint.current = null;
    preShapeImage.current = null;
  };

  const tools: { id: Tool; icon: any; label: string }[] = [
    { id: "pen", icon: Pencil, label: "قلم" },
    { id: "eraser", icon: Eraser, label: "ممحاة" },
    { id: "text", icon: Type, label: "نص" },
    { id: "line", icon: Minus, label: "خط" },
    { id: "rect", icon: Square, label: "مربع" },
    { id: "circle", icon: Circle, label: "دائرة" },
  ];

  const content = (
    <div className={`space-y-2 ${className}`} ref={containerRef}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 p-2 bg-muted/50 rounded-lg border">
        {/* Tools */}
        <div className="flex items-center gap-0.5 border-l pl-2 ml-1">
          {tools.map((t) => (
            <Button key={t.id} variant={tool === t.id ? "default" : "ghost"} size="icon" className="h-8 w-8"
              onClick={() => setTool(t.id)} title={t.label}>
              <t.icon className="w-4 h-4" />
            </Button>
          ))}
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 border-l pl-2 ml-1">
          {COLORS.map((c) => (
            <button key={c}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? "scale-125 border-primary ring-2 ring-primary/30" : "border-muted-foreground/20"}`}
              style={{ backgroundColor: c }} onClick={() => setColor(c)} />
          ))}
        </div>

        {/* Sizes */}
        <div className="flex items-center gap-1 border-l pl-2 ml-1">
          {SIZES.map((s) => (
            <button key={s}
              className={`rounded-full bg-foreground transition-transform ${lineWidth === s ? "ring-2 ring-primary/50 scale-110" : ""}`}
              style={{ width: s + 8, height: s + 8 }} onClick={() => setLineWidth(s)} />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 mr-auto">
          {/* Upload bg image/pdf */}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => fileInputRef.current?.click()} title="إضافة صورة أو PDF">
            <ImagePlus className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleUndo} disabled={undoStack.length === 0} title="تراجع">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRedo} disabled={redoStack.length === 0} title="إعادة">
            <Redo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearCanvas} title="مسح الكل">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={downloadCanvas} title="تحميل">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsFullscreen(!isFullscreen)} title={isFullscreen ? "تصغير" : "تكبير"}>
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Background file indicator */}
      {bgFileName && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg text-xs">
          <ImagePlus className="w-3.5 h-3.5 text-primary" />
          <span className="text-primary font-medium truncate flex-1">{bgFileName}</span>
          <button onClick={removeBg} className="text-destructive hover:underline text-xs">إزالة</button>
        </div>
      )}

      {/* Canvas */}
      <div className="rounded-lg border-2 border-dashed border-muted-foreground/20 overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full touch-none"
          style={{ height: isFullscreen ? `calc(100vh - 120px)` : height, cursor: tool === "eraser" ? "crosshair" : tool === "text" ? "text" : "crosshair" }}
          onMouseDown={handleStart} onMouseMove={handleMove} onMouseUp={handleEnd} onMouseLeave={handleEnd}
          onTouchStart={handleStart} onTouchMove={handleMove} onTouchEnd={handleEnd}
        />
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleBgUpload} />
    </div>
  );

  if (isFullscreen) {
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-background p-3 flex flex-col">
          {content}
        </motion.div>
      </AnimatePresence>
    );
  }

  return content;
}
